import React from "react";
import styled from "styled-components";
import { createRoot } from "react-dom/client";
import { 
    LottieLoadingIndicator
} from "src/components";
import { portalColors } from "./BusinessPortalComponents";

const MODAL_CONTAINER_ID = "modal-container";

const Body = styled.div`
    width: 48rem;
    max-height: 75vh;
    max-width: 90vw;
    pointer-events: auto;       
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
    border-radius: 16px;
    background: ${portalColors.surface};
    color: ${portalColors.text};
    box-shadow: 0 28px 70px rgba(0, 0, 0, 0.28);
    border: 1px solid ${portalColors.borderStrong};
    padding: 1.4rem;
    margin: auto;
    overflow: auto;
    animation: modal-panel-enter 0.18s ease both;

    @keyframes modal-panel-enter {
        from {
            opacity: 0;
            transform: translateY(8px) scale(0.99);
        }

        to {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }

    @media (max-width: 560px) {
        width: calc(100vw - 2rem);
        max-height: 86vh;
        padding: 1.1rem;
    }
`;

const DismissButton = styled.div`     
    pointer-events: none; 
    position: relative;
    height: 1rem;
    width: 1rem;
    
    &::before,
    &::after {
        position: absolute;
        background: ${portalColors.text};
        content: "";
        top: 0.45rem;
        left: 0;
        height: 0.125rem;
        width: 1rem;
        border-radius: .2rem;
    }

    &::before {
        transform: rotate(45deg);
    }

    &::after {
        transform: rotate(-45deg);
    }
`;

const DismissButtonContainer = styled.div`    
    align-self: flex-end;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
    cursor: pointer; 
    height: 2.35rem;
    width: 2.35rem;
    border: 1px solid ${portalColors.border};
    border-radius: 999px;
    background: ${portalColors.surfaceSoft};
    transition: background 0.16s ease, border-color 0.16s ease, transform 0.16s ease;

    &:hover {
        background: ${portalColors.surface};
        border-color: ${portalColors.borderStrong};
        transform: translateY(-1px);
    }
`;

const ModalContainer = styled.div`
    position: fixed;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 996;  
    width: 100%;
    height: 100%;  
    pointer-events: none; 
    padding: 1rem;
    box-sizing: border-box;
`;

const Container = styled.div`
    position: fixed;
    top: 0; 
    left: 0; 
    width: 100vw;
    height: 100vh;
    z-index: 994;
    
    animation-timing-function: ease-out;
    animation-name: fade-in;
    animation-duration: .1s;
    animation-fill-mode: forwards;
    
    @keyframes fade-in {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
`;

const Overlay = styled.div`
    position: fixed;
    top: 0; 
    left: 0; 
    background-color: rgba(15, 23, 42, 0.48);
    z-index: 995;  
    width: 100%;
    height: 100%;  
    pointer-events: auto;
    cursor: ${ p => (p.dismissable && "pointer") || "normal" };
`;

const ActionButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding-top: 0.35rem;

    @media (max-width: 560px) {
        flex-direction: column-reverse;

        > * {
            width: 100%;
            margin-right: 0;
        }
    }
`;

const ActionButton = styled.div`
    display: inline-block;
    padding: 0 1rem;
    min-width: 9rem;
    height: 2.5rem;
    
    background: ${portalColors.blue};
    box-shadow: none;
    border-radius: 8px;
    
    text-align: center;
    font: 700 0.875rem Manrope, Helvetica, sans-serif;
    letter-spacing: 0.35px;
    line-height: 2.5rem;
    color: var(--calendar-bg, #FFFFFF);
    
    pointer-events: auto;
    cursor: pointer;  
    user-select: none;
    background-color: ${ p => p.backgroundColor || "#51315D"};
    transition: transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 10px 22px rgba(214, 69, 69, 0.18);
    }
`;

const CancelButton = styled.div`
    display: inline-block;
    padding: 0 1rem;
    min-width: 9rem;
    height: 2.5rem;
    box-shadow: none; 
    text-align: center;
    font: 700 0.875rem Manrope, Helvetica, sans-serif;
    letter-spacing: 0.35px;
    line-height: 2.5rem;
    pointer-events: auto;
    cursor: pointer;  
    user-select: none;

    color: ${ p => p.color || "#FFFFFF"};

    background: #171A22;
    background-color: ${ p => p.backgroundColor || "#51315D"};

    border-radius: 8px;
    border:  ${ p => p.border || "1px solid #000000;"};
    margin-right: 0;
    transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 10px 22px rgba(23, 26, 34, 0.1);
    }
`;

const Header = styled.div`
    margin: -2.35rem 3rem 0;
    text-align: center;
    font: 800 1.75rem Manrope, Helvetica, sans-serif;
    letter-spacing: 0;
    color: ${portalColors.text};

    @media (max-width: 560px) {
        margin: 0;
        font-size: 1.45rem;
    }
`;

const TextBody = styled.div`
    text-align: center; 
    font: 500 1rem Manrope, Helvetica, sans-serif;
    letter-spacing: 0;
    color: ${portalColors.mutedText};
    line-height: 1.55;
    margin: 0 auto;
    max-width: 38rem;
`;

const createModal = (title, message, callback, actionName, easyDismiss, modalLoading) => {
    const modalContainer = document.createElement("div");

    ConfirmModal.instance.appendChild(modalContainer);
    const root = createRoot(modalContainer);
    ConfirmModal.roots.set(modalContainer, root);

    const dismissEvent = () => {
        root.unmount();
        ConfirmModal.roots.delete(modalContainer);
        modalContainer.parentNode?.removeChild(modalContainer);
    };

    const modalBody = (loading) => {
        return (
            <Body>
                <DismissButtonContainer onClick={dismissEvent}>
                    <DismissButton/>
                </DismissButtonContainer>
                <Header>
                    {title}
                </Header>
                <TextBody>
                    {message}
                </TextBody>

                {
                    loading ?
                        <LottieLoadingIndicator height="20px" width="20px"/>
                        :
                        <ActionButtonContainer>
                            <CancelButton backgroundColor="#FFFFFF" color="#000" onClick={dismissEvent}>
                        Cancel
                            </CancelButton>
                            <ActionButton backgroundColor="#ff6464" onClick={callback}>
                                {actionName}
                            </ActionButton>
                        </ActionButtonContainer>
                }
                
            </Body>
        );
    };

    root.render(
        <Container>
            <Overlay onClick={easyDismiss ? dismissEvent : null} dismissable={easyDismiss}/>
            <ModalContainer>
                { modalBody(modalLoading) }
            </ModalContainer>
        </Container>
    );
};

class ConfirmModal {
    static instance;
    static roots = new Map();

    static init() {
        let exists = document.getElementById(MODAL_CONTAINER_ID);

        if (exists) {
            ConfirmModal.instance = exists;
            ConfirmModal.instance.id = MODAL_CONTAINER_ID;
            return;
        }

        ConfirmModal.instance = document.createElement("div");
        ConfirmModal.instance.id = MODAL_CONTAINER_ID;
        document.body.appendChild(ConfirmModal.instance);
    }

    /** There can only every be one modal active at a time. */
    static open(title = "", message = "", callback = null, actionName = null, easyDismiss = true) {
        if (!ConfirmModal.instance || !document.getElementById(MODAL_CONTAINER_ID)) {
            ConfirmModal.init();

            console.log("ConfirmModal.init();", ConfirmModal.instance, !document.getElementById(MODAL_CONTAINER_ID));
        }

        createModal(title, message, callback, actionName, easyDismiss);
    }

    /** Allows for programmatic closing of a modal. */
    static close() {
        if (!ConfirmModal.instance || !document.getElementById(MODAL_CONTAINER_ID)) {
            console.warn("There is no modal to close.");
            return;
        }

        let modal = document.getElementById(MODAL_CONTAINER_ID);
        if (modal) {
            ConfirmModal.roots.forEach(root => root.unmount());
            ConfirmModal.roots.clear();
            modal.parentNode?.removeChild(modal);
        }
    }
}

export default ConfirmModal;
